import { Group } from "konva/lib/Core";
import { IconSize, ContainerSize, ContainerType, newContainer, newEmoji } from "./utils";
import { Actions } from "./actions";
import { ClicksCount, Reactions, ReactionStatus, ReactionWrapperPositionAdd, ReactionWrapperSize, REACTION_WRAPPER_GAP } from "./reactions";
import { Image } from "konva/lib/shapes/Image";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
import { IUIError } from "@gtmhub/error-handling/models";
import { getCurrentUserId } from "@gtmhub/users/current-user-storage";
import { IDraftGoalState, INoteState, IShapeReaction, WhiteboardShape } from "../../shapes";
import { IWhiteboardRendererContext } from "@gtmhub/whiteboards";
import { ICreateReaction } from "@gtmhub/experiments/models/models";
import { IActiveReaction } from "@gtmhub/whiteboards/models";
import { fireStateEvent } from "../../events";
import { capitalize } from "@gtmhub/util";
import { KonvaEventObject } from "konva/types/Node";

import uniounurl from "../../../../../../../wwwroot/fe/img/icons/voting/union.svg";

export enum TopPadding {
  HEIGHT = 6,
  WIDTH = 24,
}

export const EXPOSITION = 1000;

export enum MaxReactionsPerUserAllowed {
  DEFAULT = 32,
  RESTRICTED = 1,
}

export function voting(shape: WhiteboardShape<INoteState | IDraftGoalState>, context: IWhiteboardRendererContext) {
  new Voting(shape, context);
}

export class Voting {
  private votingControl: Group;
  private actionsContainer: Group;
  private reactionIconContainer: Group;
  private reactionsContainer: Group;
  private activeReactions: IActiveReaction[];
  private actionsOpened: boolean;

  private get shapeWidth(): number {
    const shapeChildren = this.shape.getChildren();
    return shapeChildren.length ? shapeChildren[0].width() : 0;
  }

  private reactionsEnabled = false;
  private maxReactionsPerUserAllowed = MaxReactionsPerUserAllowed.DEFAULT;

  private reactions: IShapeReaction[] = [];
  private shapeHoverTimeoutId: ReturnType<typeof setTimeout>;

  constructor(private shape: WhiteboardShape<INoteState | IDraftGoalState>, private context: IWhiteboardRendererContext) {
    this.reactionsEnabled = this.context.reactionSettings?.isActive;
    this.activeReactions = [...this.context.reactionSettings.activeReactions];
    this.maxReactionsPerUserAllowed = this.context.reactionSettings?.isOneReactionPerItem ? MaxReactionsPerUserAllowed.RESTRICTED : MaxReactionsPerUserAllowed.DEFAULT;
    this.reactions = this.shape.state.reactions;

    if (this.reactionsEnabled && this.reactions?.length) {
      setTimeout(() => this.build());
    }

    this.setShapeEvents();
    this.setWhiteboardEvents();
  }

  private build(): void {
    // TODO: Next iteration: Refactor this to be generic (not to depend on the shape type)
    const posY = this.shape.state.type === "note" ? this.shape.state.size?.height : this.shape.getChildren()[0].height() + ContainerSize.HEIGHT;

    this.shape
      .find("#single-reaction-group")
      .toArray()
      .forEach((g) => clearTimeout(g.getAttr("timeoutId")));

    this.shape
      .find("#voting-control-group")
      .toArray()
      .forEach((g) => g.destroy());

    this.votingControl = newContainer({ id: "voting-control-group", y: posY, name: "voting" }, ContainerType.Group) as Group;
    this.votingControl.add(newContainer({ name: "voting", width: this.shapeWidth, height: TopPadding.HEIGHT, fill: "" }));

    const isNotSelected = !this.shape.selected() && this.shape.isListening();
    if (!this.isReactionLimitReached() && isNotSelected) {
      this.buildReactionIcon();
    }
    if (this.reactions?.length) {
      this.loadReactions();
    }

    this.shape.add(this.votingControl);
    this.shape.getLayer().batchDraw();
  }

  private loadReactions() {
    this.reactions.forEach((reaction, index) => {
      const emoji = newEmoji({
        id: reaction.name,
        text: reaction.emoji,
      });

      this.buildReactions({
        emoji,
        numberOfReactions: reaction.userIds.length,
        reactionsCount: index,
        reactionStatus: this.getReactionStatus({ emoji, isEnabled: this.activeReactions.some((e) => e.name === reaction.name) }),
      });
    });

    this.rearrange();
    this.shape.getLayer().batchDraw();
  }

  private getReactionStatus(params: { emoji: Text; isEnabled: boolean }): ReactionStatus {
    const isSelected = this.shape.selected() || !this.shape.isListening();
    if (!params.isEnabled || isSelected) {
      return ReactionStatus.DISABLED;
    }

    if (this.hasCurrentUserReacted(params.emoji)) {
      return ReactionStatus.ACTIVE;
    }

    if (this.isReactionLimitReached()) {
      return ReactionStatus.DISABLED;
    }

    return ReactionStatus.DEFAULT;
  }

  private buildReactionIcon(): void {
    if (this.reactionIconContainer?.children?.length) {
      this.reactionIconContainer.children.toArray().forEach((r) => r.destroy());
    }

    this.reactionIconContainer = newContainer({ id: "reaction-icon-group", y: TopPadding.HEIGHT, name: "voting" }, ContainerType.Group) as Group;

    Image.fromURL(uniounurl, (reactionIcon: Image) => {
      reactionIcon.id("reaction-icon");
      reactionIcon.name("voting-action voting");
      reactionIcon.position({
        x: 0,
        y: ContainerSize.HEIGHT / 2 - IconSize.HEIGHT / 2,
      });
      reactionIcon.size({ width: IconSize.WIDTH, height: IconSize.HEIGHT });

      //TODO: Next iterration - attach a single click handler on the container and then determine the clicked icon
      reactionIcon.on("click", () => {
        if (this.actionsOpened) {
          this.context.setPreventDblClick(true);
          this.closeActions();
        } else {
          this.buildActions();
        }
      });

      const reactionIconWrapper = newContainer({
        name: "voting",
        width: ContainerSize.WIDTH,
        height: ContainerSize.HEIGHT,
        fill: "",
        cornerRadius: 0,
        x: 0,
      }) as Rect;

      this.reactionIconContainer.add(reactionIconWrapper, reactionIcon);
      this.votingControl.add(this.reactionIconContainer);
      this.shape.getLayer()?.batchDraw();
    });
  }

  private buildActions(): void {
    this.actionsContainer = new Actions(this.activeReactions).build();
    this.actionsOpened = true;

    this.shape.getStage().on("close-voting", (ev) => {
      const isNotCurrentShape = ev["shape"]?.state.id !== this.shape.state.id;
      if (this.actionsContainer && this.actionsOpened && isNotCurrentShape) {
        this.closeActions();
      }
    });

    if (this.actionsContainer) {
      this.votingControl.add(this.actionsContainer);
      this.rearrange();
      this.shape.getLayer().batchDraw();
    }
  }

  private buildReactions(params: { emoji: Text; numberOfReactions: number; reactionsCount: number; reactionStatus: ReactionStatus }) {
    this.reactionsContainer = new Reactions(this.shape, this.context).build(params);

    if (this.reactionsContainer) {
      this.votingControl.add(this.reactionsContainer);
      this.actionsContainer?.destroy();
    }
  }

  private restoreZIndex() {
    this.shape.zIndex(this.shape.state.zIndex);
    setTimeout(() => this.shape.getStage().batchDraw(), 100);
  }

  private react(e) {
    if (this.isReactionLimitReached()) {
      if (this.maxReactionsPerUserAllowed !== MaxReactionsPerUserAllowed.RESTRICTED) {
        this.showError("You reached the maximum number of reactions.", "Emotions… we all have them. For performance reasons however, we set a limit of 32 reactions.");
      }
      return;
    }

    if (!this.activeReactions.some((emoji) => emoji.name === e.emoji.attrs.id)) {
      return;
    }

    this.shape.zIndex(this.shape.state.zIndex);

    const createReaction: ICreateReaction = {
      name: e.emoji.attrs.id,
      emoji: e.emoji.attrs.text,
      targetId: this.shape.state.bsonId,
      targetType: "whiteboard" + capitalize(this.shape.state.type),
      targetParentId: this.context.whiteboardId,
    };

    this.context
      .createReaction(createReaction)
      .then((e) => this.updateLocalStateAfterReacting(e))
      .finally(() => this.restoreZIndex());
  }

  private unreact(e): void {
    if (!this.activeReactions.some((emoji) => emoji.name === e.emoji.attrs.id)) {
      return;
    }

    this.shape.zIndex(this.shape.state.zIndex);
    this.context
      .deleteReaction(e.emoji.attrs.id, this.shape.state.bsonId)
      .then(() => this.updateLocalStateAfterUnreacting(e))
      .finally(() => {
        setTimeout(() => this.hideTooltip(), 100);
        this.restoreZIndex();
        if (this.shape.state.reactions?.length === 0) {
          setTimeout(() => this.build(), 100);
        }
      });
  }

  private hideTooltip(): void {
    const tooltipEl = document.getElementById("tooltip");
    tooltipEl.style.visibility = "hidden";
  }

  private updateLocalStateAfterReacting(e): void {
    const reactionIndex = this.shape.state.reactions?.findIndex((r) => r.emoji === e.emoji);

    const reaction = {
      userIds: [e.userId],
      name: e.name,
      emoji: e.emoji,
      targetId: e.targetId,
    };

    if (reactionIndex == undefined) {
      this.shape.state.reactions = [reaction];
    } else if (reactionIndex == -1) {
      this.shape.state.reactions.push(reaction);
    } else {
      this.shape.state.reactions[reactionIndex].userIds = [...this.shape.state.reactions[reactionIndex].userIds, e.userId];
    }

    fireStateEvent("stateChange", e, this.shape, { state: this.shape.state, changes: { reactions: this.shape.state.reactions } });
  }

  private updateLocalStateAfterUnreacting(e): void {
    const reactionIndex = this.shape.state.reactions?.findIndex((r) => r.emoji === e.emoji.attrs.text);
    if (reactionIndex != undefined && reactionIndex > -1) {
      this.shape.state.reactions[reactionIndex].userIds = this.shape.state.reactions[reactionIndex].userIds.filter((id) => id !== getCurrentUserId());
      this.shape.state.reactions = this.shape.state.reactions.filter((r) => r.userIds.length > 0);

      fireStateEvent("stateChange", e, this.shape, { state: this.shape.state, changes: { reactions: this.shape.state.reactions } });
    }
  }

  private hasCurrentUserReacted(reaction: Text): boolean {
    const userAlreadyReacted = this.reactions?.some((r) => r.name === reaction.attrs.id && r.userIds.includes(getCurrentUserId()));
    const reactionIsActive = this.activeReactions?.some((r) => r.name === reaction.attrs.id);

    return this.reactions && userAlreadyReacted && reactionIsActive;
  }

  private isReactionLimitReached(): boolean {
    if (!this.reactions) return false;

    const currentUserReactionCount = this.reactions.reduce((acc, current) => {
      const reactionIsActive = this.activeReactions.some((a) => a.emoji === current.emoji);

      if (current.userIds.includes(getCurrentUserId()) && reactionIsActive) {
        return acc + 1;
      }
      return acc;
    }, 0);
    return this.maxReactionsPerUserAllowed <= currentUserReactionCount;
  }

  private showError(title: string, message: string, body = ""): void {
    const error: IUIError = {
      title,
      message,
      body,
      connectionError: true,
    };

    this.context.showErrorModal(error);
  }

  private rearrange(): void {
    const unsortedReactions = this.votingControl.find(".reaction-wrapper").toArray();

    const sortedReactions =
      this.shape.state.reactions?.map((r) => {
        return unsortedReactions.find((shape) => shape.parent.findOne(`#${r.name}`));
      }) || [];

    if (unsortedReactions.length > sortedReactions.length) {
      sortedReactions.push(unsortedReactions[unsortedReactions.length - 1]);
    }

    const singleReactionWidth = ReactionWrapperSize.WIDTH + REACTION_WRAPPER_GAP;

    let rowsCount = 0;

    const reactionsPerRow = Math.ceil((this.shapeWidth - ContainerSize.WIDTH - REACTION_WRAPPER_GAP) / singleReactionWidth);

    let lastReactionItem = null;

    for (let i = 0; i < sortedReactions.length; i += reactionsPerRow) {
      let nextXPos = 0;
      const chunk = sortedReactions.slice(i, i + reactionsPerRow).filter((c) => c);

      chunk.forEach((r) => {
        r.y(rowsCount * ReactionWrapperSize.HEIGHT + (i > 0 ? REACTION_WRAPPER_GAP * rowsCount : 0));
        r.x(nextXPos);

        // adjust the reaction container
        r.parent.children[0].y(r.y());
        r.parent.children[0].x(r.x());

        // adjust the emoji icon position
        r.parent.children[2].y(r.y() + ReactionWrapperPositionAdd.Y);
        r.parent.children[2].x(r.x() + ReactionWrapperPositionAdd.X);

        // adjust the count of votes/reactions position
        r.parent.children[3].y((r.y() + ClicksCount.OFFSET_Y) as number);
        r.parent.children[3].x((r.x() + ClicksCount.OFFSET_X) as number);

        lastReactionItem = r;
        nextXPos = r.x() + r.width() + REACTION_WRAPPER_GAP;
      });
      if (i > 0) {
        this.reactionsContainer.add(
          newContainer(
            {
              y: rowsCount * ReactionWrapperSize.HEIGHT,
              fill: "",
              width: this.shapeWidth,
              height: REACTION_WRAPPER_GAP,
              name: "voting",
            },
            ContainerType.Wrapper
          ) as Rect
        );
      }

      rowsCount++;
    }

    if (this.reactionIconContainer) {
      const finalYPos = (lastReactionItem?.y() || 0) + ContainerSize.HEIGHT / 2 - REACTION_WRAPPER_GAP;
      if (lastReactionItem) {
        this.reactionIconContainer.y(finalYPos);
        this.reactionIconContainer.x(lastReactionItem.x() + ReactionWrapperSize.WIDTH + REACTION_WRAPPER_GAP);
      }

      if (this.actionsContainer) {
        this.actionsContainer.y(finalYPos);
        this.actionsContainer.x(this.reactionIconContainer.x() + ContainerSize.WIDTH / 2 + IconSize.WIDTH / 2);
      }
    }
  }

  private demolish(): void {
    if (this.votingControl) {
      this.votingControl.destroy();
      this.shape.getLayer().batchDraw();
    }
  }

  private setWhiteboardEvents() {
    this.context.events.onReactionsEnabled(() => {
      this.reactionsEnabled = true;
      this.build();

      if (!this.reactions?.length) {
        setTimeout(() => this.demolish(), EXPOSITION);
      }
    });

    this.context.events.onReactionsDisabled(() => {
      this.reactionsEnabled = false;
      setTimeout(() => this.demolish());
    });

    this.context.events.onReactionSettingsChanged((args) => {
      if (!this.reactions?.length) {
        this.activeReactions = [...args.payload.activeReactions];
        return;
      }

      let reactionAvailabilityWasChanged = false;
      for (const reaction of this.reactions) {
        const isPresent = args.payload.activeReactions?.some((emoji) => emoji.name === reaction.name);
        const wasPresent = this.activeReactions?.some((emoji) => emoji.name === reaction.name);

        const removed = !isPresent && wasPresent;
        const added = isPresent && !wasPresent;

        if (removed || added) {
          reactionAvailabilityWasChanged = true;
        }
      }

      this.activeReactions = [...args.payload.activeReactions];

      if (reactionAvailabilityWasChanged && args.payload.isActive) {
        this.build();
      }
    });

    this.context.events.onShapeReaction((args) => {
      if (args.payload.targetId === this.shape.state.bsonId) {
        this.reactions = args.payload.reactions;
        if (this.reactions?.length) {
          this.build();
        } else {
          this.demolish();
        }
      }
    });

    this.context.events.onMaxReactionsPerUserAllowedChanged((maxCount) => {
      this.maxReactionsPerUserAllowed = maxCount;
      if (this.votingControl?.hasChildren()) {
        this.build();
      }
    });

    this.context.events.onLockWhiteboard(() => {
      if (this.reactions?.length) {
        this.build();
      }
    });
  }

  private setShapeEvents() {
    this.attachEvent(["mouseenter"], () => {
      if (!this.shape.selected() && this.reactionsEnabled && !this.reactions?.length && !this.actionsOpened) {
        clearTimeout(this.shapeHoverTimeoutId);
        setTimeout(() => this.build());
      }
    });

    this.attachEvent(["dblclick"], () => {
      this.context.setPreventDblClick(true);
    });

    this.attachEvent(["mouseleave"], () => {
      if (this.reactionsEnabled && !this.reactions?.length && !this.actionsOpened) {
        this.shapeHoverTimeoutId = setTimeout(() => {
          this.demolish();
        }, 500);
      }
    });

    this.attachEvent(["reaction-taken"], (e) => {
      this.hasCurrentUserReacted(e["emoji"]) ? this.unreact(e) : this.react(e);
      setTimeout(() => (document.body.style.cursor = "default"));
    });

    this.attachEvent(["shape-unselected", "resize"], () => {
      if (this.shape.isVisible()) {
        this.buildVoting();
      }
    });

    this.attachEvent(["shape-selected"], () => {
      if (this.reactionsEnabled && this.reactions?.length) {
        this.build();

        this.shape
          .find(".voting")
          .toArray()
          .forEach((g) => g.listening(false));

        this.shape.getLayer().batchDraw();
      } else {
        this.demolish();
      }
    });

    this.shape.on("transform transformstart", () => {
      setTimeout(() => this.demolish());
    });
  }

  private attachEvent(eventNames: string[], eventHandler: (event?: KonvaEventObject<Event>) => void) {
    eventNames.forEach((eventName) => {
      if (this.shouldAttachVotingEvent(eventName)) {
        this.shape.on(eventName + ".voting", eventHandler);
      }
    });
  }

  private shouldAttachVotingEvent(name: string): boolean {
    const hasEventHandler = name in this.shape.eventListeners;
    const hasVotingEventHandler = this.shape.eventListeners[name]?.some((e) => e.name === "voting");
    return !hasEventHandler || !hasVotingEventHandler;
  }

  private buildVoting() {
    if (this.reactionsEnabled && this.reactions?.length) {
      this.build();
    }
  }

  private closeActions(): void {
    this.actionsOpened = false;
    this.actionsContainer.destroy();
    this.shape.getLayer().batchDraw();
    document.body.style.cursor = "default";
    this.restoreZIndex();
    if (!this.reactions?.length) this.demolish();
  }
}
